import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { useClientsQuery } from '../../api/queries';
import PreviewDialog from './PreviewDialog';
import { useConfigSectionEditor } from './useConfigSectionEditor';
import {
  SaveSuccessSnackbar,
  SectionAlerts,
  SectionProvenance,
  SectionTitle,
  UnsavedChangesGuard,
} from './SectionEditorChrome';

const identity = (clients: string[]): string[] => clients;

/**
 * Huntress-Only Clients settings section.
 *
 * Displays the list of clients where Huntress is the sole RMM (not Automate).
 * Devices for these clients are not flagged as orphaned. Supports add via
 * autocomplete (populated from useClientsQuery) and remove via chip delete.
 * Preview-before-apply via PreviewDialog.
 */
export default function HuntressOnlySection() {
  const editor = useConfigSectionEditor<string, string[]>({
    section: 'huntress-only-clients',
    label: 'Huntress-Only Clients',
    toRows: identity,
    fromRows: identity,
  });
  const clientList = editor.rows;
  const setClientList = editor.setRows;
  const clientsQuery = useClientsQuery();

  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');

  // Extract distinct client names from the clients query for autocomplete suggestions
  const clientOptions: string[] = clientsQuery.data
    ? [...new Set(clientsQuery.data.map((c) => c.clientName))].sort()
    : [];

  function handleAddClient(name: string | null) {
    if (!name) return;
    const trimmed = name.trim();
    const alreadyListed = clientList.some((c) => c.toLowerCase() === trimmed.toLowerCase());
    if (trimmed && !alreadyListed) {
      setClientList((prev) => [...prev, trimmed]);
    }
    setAutocompleteValue(null);
    setInputValue('');
  }

  function handleRemoveClient(name: string) {
    setClientList((prev) => prev.filter((c) => c !== name));
  }

  if (editor.isLoading && !editor.data) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={200} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle title="Huntress-Only Clients" isDirty={editor.isDirty} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Devices for these clients will not be flagged as orphaned. Names must match the canonical
        client name after alias resolution.
      </Typography>

      <SectionAlerts
        loadError={editor.loadError}
        hasConflict={editor.hasConflict}
        validationError={editor.validationError}
        onDiscard={editor.discardChanges}
      />

      {/* Chip list */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40 }}>
        {clientList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No clients configured
          </Typography>
        ) : (
          clientList.map((name) => (
            <Chip key={name} label={name} onDelete={() => handleRemoveClient(name)} size="small" />
          ))
        )}
      </Box>

      {/* Autocomplete to add a client */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
        <Autocomplete
          freeSolo
          options={clientOptions}
          value={autocompleteValue}
          inputValue={inputValue}
          onChange={(_e, newValue) => {
            if (typeof newValue === 'string') {
              handleAddClient(newValue);
            }
          }}
          onInputChange={(_e, newInput) => setInputValue(newInput)}
          size="small"
          sx={{ width: 280 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add client"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddClient(inputValue);
                }
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleAddClient(inputValue)}
          sx={{ mt: 0.5 }}
        >
          Add
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => editor.openPreview()}
          disabled={editor.preview.isPending || !editor.isDirty}
        >
          {editor.preview.isPending ? 'Computing...' : 'Preview Changes'}
        </Button>
        {editor.isDirty && (
          <Button size="small" color="inherit" onClick={editor.discardChanges}>
            Discard
          </Button>
        )}
      </Box>

      <SectionProvenance record={editor.record} />

      <PreviewDialog
        open={editor.preview.open}
        preview={editor.preview.data}
        previewLoading={editor.preview.isPending}
        error={editor.preview.error}
        applying={editor.applying}
        applyError={editor.applyError}
        writeDisabledReason={editor.writeDisabledReason}
        onApply={() => void editor.apply()}
        onCancel={editor.closePreview}
      />
      <SaveSuccessSnackbar message={editor.successMessage} onClose={editor.clearSuccess} />
      <UnsavedChangesGuard when={editor.isDirty} />
    </Box>
  );
}
